import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Papa from 'papaparse';
import { vendorReport } from '../../actions/invoiceDateActions';
import { 
    FileDown, Search, Loader2, List, Hash, Radio, PhoneCall,
    BarChart3, Calendar, ChevronLeft, ChevronRight, FileText
} from 'lucide-react';

const VendorReports = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openstart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [typeFilter, setTypeFilter] = useState('All');
    const [hasSearched, setHasSearched] = useState(false);
    const dispatch = useDispatch();

    const formatDateToYYYYMMDD = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const { filteredData, totalCount, arsCount, d2dCount } = useMemo(() => {
        let arsCount = 0;
        let d2dCount = 0;

        for (const row of reportData) {
            if (row.inputMethod === 'ARS') arsCount++;
            else d2dCount++;
        }

        const filtered = typeFilter === 'All'
            ? reportData
            : reportData.filter(row => row.inputMethod === typeFilter);

        return { filteredData: filtered, totalCount: reportData.length, arsCount, d2dCount };
    }, [reportData, typeFilter]);

    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            setError("Please select both start and end dates");
            return;
        }
        const formattedStartDate = formatDateToYYYYMMDD(startDate);
        const formattedEndDate = formatDateToYYYYMMDD(endDate);
        setIsLoading(true);
        setError(null);
        setTypeFilter('All');
        setHasSearched(true);

        dispatch(vendorReport(formattedStartDate, formattedEndDate))
            .then(invoiceData => {
                setReportData(invoiceData);
                setIsLoading(false);
                setPage(0);
            })
            .catch(err => {
                setError("Failed to fetch report data");
                setIsLoading(false);
            });
    };

    const handleExport = () => {
        if (!filteredData.length) {
            setError("No data to export");
            return;
        }
        const formattedStartDate = formatDateToYYYYMMDD(startDate);
        const formattedEndDate = formatDateToYYYYMMDD(endDate);
        const suffix = typeFilter !== 'All' ? `_${typeFilter}` : '';
        const csv = Papa.unparse(filteredData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `ARS_Usage_Report${suffix}_${formattedStartDate}_${formattedEndDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleInvoiceClick = (invoiceId) => {
        window.open(`/invoice/${invoiceId}`, '_blank');
    };

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setPage(0);
    };

    const toggleShowAll = () => {
        setShowAll(!showAll);
        setPage(0);
    };

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // ──────── Count Card ────────
    const CountCard = ({ label, count, icon: Icon, bgColor, iconBg, textColor, isActive, onClick }) => (
        <button
            onClick={onClick}
            className={`
                flex-1 min-w-[160px] flex items-center gap-4 px-5 py-4 rounded-xl transition-all text-left
                ${isActive 
                    ? `${bgColor} ring-2 ring-offset-1 ring-current shadow-sm` 
                    : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'}
            `}
        >
            <div className={`p-2.5 rounded-lg ${isActive ? iconBg : 'bg-gray-100'}`}>
                <Icon className={`w-5 h-5 ${isActive ? textColor : 'text-gray-500'}`} />
            </div>
            <div>
                <div className={`text-2xl font-bold ${isActive ? textColor : 'text-gray-900'}`}>
                    {count}
                </div>
                <div className={`text-xs font-semibold uppercase tracking-wider ${isActive ? textColor + ' opacity-70' : 'text-gray-400'}`}>
                    {label}
                </div>
            </div>
        </button>
    );

    // ──────── Empty State (before search) ────────
    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                <BarChart3 className="w-10 h-10 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Generate Your Report
            </h3>
            <p className="text-gray-400 text-center max-w-sm mb-1">
                Select a date range above and click <strong>Go</strong> to view tow usage data with ARS and D2D breakdowns.
            </p>
        </div>
    );

    // ──────── No Results State (after search, 0 results) ────────
    const NoResultsState = () => (
        <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {typeFilter !== 'All' ? `No ${typeFilter} Records` : 'No Records Found'}
            </h3>
            <p className="text-gray-400 text-center max-w-sm">
                {typeFilter !== 'All' 
                    ? `No ${typeFilter} tows were found in this date range. Try selecting "All Types" to see all records.`
                    : 'No tow records found for the selected date range. Try a different range.'
                }
            </p>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-full mx-auto p-6">
                {/* ──────── Page Container (single white card) ──────── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" 
                     style={{ minHeight: 'calc(100vh - 120px)' }}>

                    {/* ──────── Header ──────── */}
                    <div className="px-8 pt-8 pb-6">
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2 bg-blue-600 rounded-lg">
                                <BarChart3 className="w-5 h-5 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                ARS Usage Report
                            </h1>
                        </div>
                        <p className="text-gray-400 text-sm ml-12">
                            View tow volume and input method breakdown by date range
                        </p>
                    </div>

                    {/* ──────── Filter Bar ──────── */}
                    <div className="px-8 pb-6">
                        <div className="flex flex-wrap items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400 hidden sm:block" />
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        open={openstart}
                                        onClose={() => setOpenStart(false)}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        slotProps={{ 
                                            textField: { 
                                                variant: 'outlined',
                                                size: 'small',
                                                onClick: () => setOpenStart(true),
                                                sx: { 
                                                    width: 170,
                                                    '& .MuiOutlinedInput-root': { 
                                                        backgroundColor: 'white',
                                                        borderRadius: '8px',
                                                        fontSize: '14px'
                                                    }
                                                }
                                            } 
                                        }}
                                    />
                                    <span className="text-gray-300 font-medium">—</span>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        open={openEnd}
                                        onClose={() => setOpenEnd(false)}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        slotProps={{ 
                                            textField: { 
                                                variant: 'outlined',
                                                size: 'small',
                                                onClick: () => setOpenEnd(true),
                                                sx: { 
                                                    width: 170,
                                                    '& .MuiOutlinedInput-root': { 
                                                        backgroundColor: 'white',
                                                        borderRadius: '8px',
                                                        fontSize: '14px'
                                                    }
                                                }
                                            } 
                                        }}
                                    />
                                </div>
                            </LocalizationProvider>

                            <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                            {/* Type Filter */}
                            <select
                                value={typeFilter}
                                onChange={(e) => handleTypeFilterChange(e.target.value)}
                                className="h-10 px-3 pr-8 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                            >
                                <option value="All">All Types</option>
                                <option value="ARS">ARS Only</option>
                                <option value="D2D">D2D Only</option>
                            </select>

                            <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                            {/* Actions */}
                            <button
                                onClick={handleGenerateReport}
                                disabled={isLoading}
                                className="inline-flex items-center h-10 px-5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Search className="w-4 h-4 mr-2" />
                                )}
                                Go
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={!filteredData.length}
                                className="inline-flex items-center h-10 px-5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <FileDown className="w-4 h-4 mr-2" />
                                Export
                            </button>
                        </div>
                    </div>

                    {/* ──────── Error ──────── */}
                    {error && (
                        <div className="mx-8 mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">
                            {error}
                        </div>
                    )}

                    {/* ──────── Summary Count Cards ──────── */}
                    {reportData.length > 0 && (
                        <div className="px-8 pb-6">
                            <div className="flex flex-wrap gap-4">
                                <CountCard
                                    label="Total Tows"
                                    count={totalCount}
                                    icon={Hash}
                                    bgColor="bg-blue-50"
                                    iconBg="bg-blue-100"
                                    textColor="text-blue-700"
                                    isActive={typeFilter === 'All'}
                                    onClick={() => handleTypeFilterChange('All')}
                                />
                                <CountCard
                                    label="ARS"
                                    count={arsCount}
                                    icon={Radio}
                                    bgColor="bg-emerald-50"
                                    iconBg="bg-emerald-100"
                                    textColor="text-emerald-700"
                                    isActive={typeFilter === 'ARS'}
                                    onClick={() => handleTypeFilterChange('ARS')}
                                />
                                <CountCard
                                    label="D2D"
                                    count={d2dCount}
                                    icon={PhoneCall}
                                    bgColor="bg-amber-50"
                                    iconBg="bg-amber-100"
                                    textColor="text-amber-700"
                                    isActive={typeFilter === 'D2D'}
                                    onClick={() => handleTypeFilterChange('D2D')}
                                />
                            </div>
                        </div>
                    )}

                    {/* ──────── Content Area ──────── */}
                    <div className="px-8 pb-8">
                        {/* Before any search — empty state */}
                        {!hasSearched && !isLoading && (
                            <EmptyState />
                        )}

                        {/* Loading spinner */}
                        {isLoading && (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                                <p className="text-gray-400 text-sm">Fetching report data...</p>
                            </div>
                        )}

                        {/* After search with 0 results */}
                        {hasSearched && !isLoading && filteredData.length === 0 && (
                            <NoResultsState />
                        )}

                        {/* Data Table */}
                        {!isLoading && filteredData.length > 0 && (
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Invoice Number
                                                </th>
                                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Service Call Date
                                                </th>
                                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Pickup Address
                                                </th>
                                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Type
                                                </th>
                                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Caller Name
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {filteredData
                                                .slice(
                                                    showAll ? 0 : page * rowsPerPage, 
                                                    showAll ? filteredData.length : page * rowsPerPage + rowsPerPage
                                                )
                                                .map((data, index) => (
                                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span 
                                                                onClick={() => handleInvoiceClick(data.invoiceId)}
                                                                className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium text-sm"
                                                            >
                                                                {data.invoiceId}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                            {data.serviceCallDate}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">
                                                            {data.pickUpAddress}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`
                                                                inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold
                                                                ${data.inputMethod === 'ARS' 
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                                                    : 'bg-amber-50 text-amber-700 border border-amber-200'}
                                                            `}>
                                                                {data.inputMethod}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                            {data.callerName}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ──────── Footer / Pagination ──────── */}
                                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                                    <div className="text-sm text-gray-500">
                                        {showAll ? (
                                            <span>Showing all <strong>{filteredData.length}</strong> results</span>
                                        ) : (
                                            <span>
                                                <strong>{page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredData.length)}</strong> of <strong>{filteredData.length}</strong>
                                            </span>
                                        )}
                                        {typeFilter !== 'All' && (
                                            <span className="text-gray-400 ml-1">
                                                ({typeFilter} filtered from {totalCount} total)
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={toggleShowAll}
                                            className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                                        >
                                            <List className="w-3.5 h-3.5 mr-1.5" />
                                            {showAll ? 'Paginate' : 'Show All'}
                                        </button>
                                        {!showAll && (
                                            <>
                                                <button
                                                    onClick={() => setPage(Math.max(0, page - 1))}
                                                    disabled={page === 0}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                                                </button>
                                                <span className="text-xs text-gray-500 min-w-[60px] text-center">
                                                    {page + 1} / {totalPages}
                                                </span>
                                                <button
                                                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                                    disabled={page >= totalPages - 1}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorReports;